import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

const d = (offsetDays: number) =>
  new Date(Date.now() + offsetDays * 86_400_000).toISOString();

export async function seedHRMSProject(token: string): Promise<string> {
  const auth = { Authorization: `Bearer ${token}` };

  // ── 1. Project ──────────────────────────────────────────────────────────────
  const { data: project } = await axios.post(
    `${BASE}/api/projects`,
    {
      name: "HRMS Modernization — Enterprise HR Platform",
      description:
        "Replace the 14-year-old PeopleSoft instance with a cloud-native HRMS serving 1,200 employees across IT, HR, and Finance. Covers payroll, onboarding, leave management, and performance reviews.",
      status: "active",
      methodology: "agile",
      sprintLength: 2,

      diagramText: `flowchart TD
  A[Project Kick-off] --> B[Stakeholder Interviews]
  B --> C[Requirements Elicitation]
  C --> D[Gap Analysis — PeopleSoft vs Target]
  D --> E{Go / No-Go Review}
  E -->|Go| F[Vendor Selection RFP]
  E -->|No-Go| C
  F --> G[Contract & Onboarding]
  G --> H1[Payroll Module Build]
  G --> H2[Leave Management Build]
  G --> H3[Onboarding Workflow Build]
  H1 --> I[System Integration Testing]
  H2 --> I
  H3 --> I
  I --> J[UAT — HR + Finance pilot]
  J -->|Pass| K[Phased Rollout]
  J -->|Fail| L[Defect Triage]
  L --> I
  K --> M[Hypercare Support 60d]
  M --> N[Retrospective + Handover]`,

      stakeholders: [
        {
          id: "sh-1",
          name: "Linda Tran",
          role: "Project Sponsor",
          team: "Executive",
          email: "ltran@corp.example",
          timezone: "AEST",
          notes: "Approves budget. Wants weekly 1-page status update.",
          interest: "On-time delivery, $0 payroll errors",
          influence: "high",
        },
        {
          id: "sh-2",
          name: "Marcus Webb",
          role: "HR Director",
          team: "Human Resources",
          email: "mwebb@corp.example",
          timezone: "AEST",
          notes: "Primary process owner. Define all leave policy rules.",
          interest: "Self-service portal for 1,200 employees",
          influence: "high",
        },
        {
          id: "sh-3",
          name: "Priya Nair",
          role: "Finance Controller",
          team: "Finance",
          email: "pnair@corp.example",
          timezone: "AEST",
          notes: "Owns payroll reconciliation. SOX compliance signoff required.",
          interest: "Zero payroll variance, audit trail",
          influence: "high",
        },
        {
          id: "sh-4",
          name: "Sam Okafor",
          role: "IT Architect",
          team: "IT",
          email: "sokafor@corp.example",
          timezone: "AEST",
          notes: "Azure AD SSO integration lead. Reviews all API contracts.",
          interest: "Clean API, no shadow IT",
          influence: "medium",
        },
        {
          id: "sh-5",
          name: "Jess Park",
          role: "Change Manager",
          team: "HR",
          email: "jpark@corp.example",
          timezone: "AEST",
          notes: "Owns adoption risk. Runs all end-user training sessions.",
          interest: "< 15% change-resistance rate at go-live",
          influence: "medium",
        },
      ],

      raciMatrix: [
        {
          id: "raci-1",
          activity: "Requirements sign-off",
          assignments: { "sh-1": "A", "sh-2": "R", "sh-3": "C", "sh-4": "I", "sh-5": "I" },
        },
        {
          id: "raci-2",
          activity: "Vendor RFP evaluation",
          assignments: { "sh-1": "A", "sh-2": "C", "sh-3": "C", "sh-4": "R", "sh-5": "I" },
        },
        {
          id: "raci-3",
          activity: "Payroll UAT sign-off",
          assignments: { "sh-1": "I", "sh-2": "C", "sh-3": "R", "sh-4": "I", "sh-5": "I" },
        },
        {
          id: "raci-4",
          activity: "End-user training rollout",
          assignments: { "sh-1": "I", "sh-2": "A", "sh-3": "I", "sh-4": "C", "sh-5": "R" },
        },
      ],

      documents: [
        {
          id: "doc-brd",
          type: "brd",
          title: "Business Requirements Document — HRMS Modernization",
          content: `# Business Requirements Document
**Project:** HRMS Modernization
**Version:** 1.2
**Status:** Approved

## Executive Summary
Replace PeopleSoft 9.1 (EOL 2025-12) with a cloud-native HRMS. Primary drivers: vendor EOL, $420K/yr maintenance cost, zero mobile support, manual payroll reconciliation consuming 3 FTE days per cycle.

## Business Objectives
1. Reduce payroll processing time by 60% (from 3 days → 1.2 days)
2. Enable employee self-service for leave, timesheets, and payslips
3. Pass SOX payroll audit with zero manual findings
4. Achieve 95%+ adoption within 90 days of go-live

## Scope In
- Payroll (monthly + fortnightly cycles)
- Leave management (18 leave types)
- Employee onboarding workflow
- Performance review cycle (annual + mid-year)
- Azure AD SSO integration
- Data migration from PeopleSoft (7 years of history)

## Scope Out
- Learning Management System (Phase 2)
- Recruitment ATS (separate procurement)
- Third-party background checks

## Success Metrics
| Metric | Baseline | Target |
|---|---|---|
| Payroll run time | 3 days | 1.2 days |
| Payroll error rate | 2.1% | < 0.1% |
| Employee self-service rate | 12% | > 80% |
| IT support tickets (HR) | 340/mo | < 50/mo |

## Risks
- **Data migration quality** — 7 years of PeopleSoft data with 3 schema versions
- **Parallel run period** — Must run both systems for 1 payroll cycle minimum
- **Change resistance** — Finance team has low digital adoption score (42%)`,
          createdAt: d(-30),
          updatedAt: d(-5),
        },
        {
          id: "doc-frd",
          type: "frd",
          title: "Functional Requirements Document — Payroll & Leave Modules",
          content: `# Functional Requirements Document
**Module:** Payroll + Leave Management
**Author:** BA Team

## Payroll Module

### FR-PAY-001: Payroll Run Initiation
System shall allow Finance Controller to initiate a payroll run for a selected pay period. The run must lock all timesheet inputs for that period before calculation begins.

### FR-PAY-002: Multi-Currency Support
System shall support AUD, USD, SGD for employees in 3 offices (Sydney, New York, Singapore).

### FR-PAY-003: Payroll Reconciliation Report
System shall generate a variance report comparing current vs. previous cycle for every employee. Any variance > $50 shall be flagged as a line item requiring manual review.

### FR-PAY-004: SOX Audit Trail
Every payroll calculation step must be logged with timestamp, user ID, and input snapshot. Logs must be immutable and retained for 7 years.

## Leave Management Module

### FR-LV-001: Leave Request Workflow
Employees shall submit leave requests via self-service portal. Requests route to direct manager for approval. Auto-escalation after 48hrs.

### FR-LV-002: Leave Balance Calculation
System shall calculate leave balances in real-time, accounting for accrual rules, approved pending leaves, and public holidays by state.

### FR-LV-003: Overlapping Leave Detection
System shall warn managers when two or more team members from the same team have overlapping leave exceeding 30% of team headcount.

### FR-LV-004: Public Holiday Calendar
System shall maintain public holiday calendars for NSW, VIC, QLD, WA, NY, SGP. HR Admin can add or modify entries.`,
          createdAt: d(-25),
          updatedAt: d(-3),
        },
      ],

      analyses: [
        {
          id: "an-1",
          techniqueId: "swot",
          techniqueName: "SWOT Analysis",
          data: {
            strengths: [
              "Executive sponsorship secured with approved $1.8M budget",
              "Experienced BA team with prior ERP migration",
              "IT team has Azure AD expertise",
            ],
            weaknesses: [
              "Finance team has low digital adoption (42% score)",
              "PeopleSoft data has 3 schema versions — migration complexity",
              "No dedicated change manager until week 6",
            ],
            opportunities: [
              "Vendor offering 20% discount for Q3 contract signing",
              "AI payroll anomaly detection in newer platforms",
              "Integration with existing Power BI dashboards",
            ],
            threats: [
              "PeopleSoft EOL Dec 2025 — hard deadline",
              "ATO payroll reporting rule change effective Jul 2025",
              "Key SME (payroll lead) has 3-week leave during UAT",
            ],
          },
          createdAt: d(-20),
          updatedAt: d(-20),
        },
        {
          id: "an-2",
          techniqueId: "moscow",
          techniqueName: "MoSCoW Prioritisation",
          data: {
            must: [
              "Payroll processing (monthly + fortnightly)",
              "SOX audit trail",
              "Azure AD SSO",
              "Data migration — 7 years employee history",
            ],
            should: [
              "Leave management self-service",
              "Manager approval workflows",
              "Payroll reconciliation report",
            ],
            could: [
              "Performance review module",
              "Mobile app for leave requests",
              "Real-time payslip push notification",
            ],
            wont: [
              "LMS integration (Phase 2)",
              "Recruitment ATS (separate project)",
            ],
          },
          createdAt: d(-18),
          updatedAt: d(-18),
        },
      ],

      userStories: [
        // DONE
        {
          id: "us-1",
          title: "Employee self-service payslip download",
          asA: "employee",
          iWant: "to download my payslip as PDF from the self-service portal",
          soThat: "I don't need to email HR every pay cycle",
          acceptanceCriteria: [
            "Payslip PDF matches official format with company logo",
            "Last 24 months of payslips available",
            "Download logged in audit trail",
            "Available within 2 hours of payroll run completion",
          ],
          priority: "must",
          storyPoints: 5,
          status: "done",
          startDate: d(-40),
          dueDate: d(-30),
          estimatedDays: 10,
        },
        {
          id: "us-2",
          title: "Leave request submission and approval",
          asA: "employee",
          iWant: "to submit a leave request that auto-routes to my manager",
          soThat: "I don't need to use email or paper forms",
          acceptanceCriteria: [
            "Leave types match 18 HR-approved categories",
            "Auto-escalation fires after 48hrs if no action",
            "Employee gets email confirmation on submit and on decision",
            "Balance deducted only on approval, not on submission",
          ],
          priority: "must",
          storyPoints: 8,
          status: "done",
          startDate: d(-35),
          dueDate: d(-22),
          estimatedDays: 13,
        },
        {
          id: "us-3",
          title: "Payroll run initiation with period lock",
          asA: "Finance Controller",
          iWant: "to initiate a payroll run that locks all timesheets for that period",
          soThat: "calculations are based on a fixed, tamper-proof input set",
          acceptanceCriteria: [
            "Period lock prevents any timesheet edits after initiation",
            "Lock event logged with user, timestamp, and pay period",
            "Finance Controller can unlock with dual-approval only",
            "System shows locked badge on all affected timesheet views",
          ],
          priority: "must",
          storyPoints: 8,
          status: "done",
          startDate: d(-28),
          dueDate: d(-16),
          estimatedDays: 12,
        },
        // IN-PROGRESS
        {
          id: "us-4",
          title: "Payroll variance reconciliation report",
          asA: "Finance Controller",
          iWant: "to see a line-by-line variance report vs the previous cycle",
          soThat: "I can review and sign off on any deviation > $50 before payments release",
          acceptanceCriteria: [
            "Report shows current, previous, and delta for every employee",
            "Rows with delta > $50 highlighted in amber",
            "Export to CSV and PDF supported",
            "Report generated within 5 minutes of payroll run completion",
          ],
          priority: "must",
          storyPoints: 5,
          status: "in-progress",
          startDate: d(-10),
          dueDate: d(4),
          estimatedDays: 14,
        },
        {
          id: "us-5",
          title: "Azure AD SSO integration",
          asA: "IT Architect",
          iWant: "all HRMS logins to authenticate through Azure AD",
          soThat: "employees use one set of credentials and IT controls access centrally",
          acceptanceCriteria: [
            "SAML 2.0 or OAuth 2.0 / OIDC flow implemented",
            "Role mapping: Employee, Manager, HR Admin, Finance Controller, Super Admin",
            "MFA inherited from Azure AD policy",
            "Deprovisioning: Azure AD disable → HRMS access revoked within 15 min",
          ],
          priority: "must",
          storyPoints: 13,
          status: "in-progress",
          startDate: d(-14),
          dueDate: d(7),
          estimatedDays: 21,
        },
        {
          id: "us-6",
          title: "Overlapping leave conflict detection",
          asA: "manager",
          iWant: "a warning when ≥30% of my team requests leave on the same dates",
          soThat: "I can proactively manage coverage before approving",
          acceptanceCriteria: [
            "Warning shown on approval screen before manager decides",
            "Threshold configurable per team by HR Admin",
            "Warning is advisory — manager can still approve",
            "HR Admin receives weekly digest of teams that triggered the threshold",
          ],
          priority: "should",
          storyPoints: 3,
          status: "in-progress",
          startDate: d(-5),
          dueDate: d(9),
          estimatedDays: 14,
        },
        // BACKLOG
        {
          id: "us-7",
          title: "Multi-currency payroll support",
          asA: "Finance Controller",
          iWant: "payroll calculated in AUD, USD, and SGD based on employee home office",
          soThat: "offshore employees receive correct local-currency payments",
          acceptanceCriteria: [
            "Exchange rates fetched daily from RBA or ECB feed",
            "Rate used for each run is locked at run initiation and logged",
            "Payslip shows local currency amount and AUD equivalent",
            "Variance report shows all amounts in AUD for comparison",
          ],
          priority: "should",
          storyPoints: 8,
          status: "backlog",
          startDate: d(10),
          dueDate: d(28),
          estimatedDays: 18,
        },
        {
          id: "us-8",
          title: "Employee onboarding workflow",
          asA: "HR Admin",
          iWant: "a checklist-driven onboarding workflow that auto-assigns IT provisioning tasks",
          soThat: "new starters are fully set up before Day 1 without manual email chains",
          acceptanceCriteria: [
            "Template with 12 default onboarding tasks",
            "IT tasks auto-assigned to IT helpdesk queue on trigger",
            "HR Admin can customise task list per department",
            "New starter gets a welcome portal with their task list and first-day info",
          ],
          priority: "must",
          storyPoints: 13,
          status: "backlog",
          startDate: d(12),
          dueDate: d(40),
          estimatedDays: 28,
        },
        {
          id: "us-9",
          title: "Performance review cycle — annual",
          asA: "HR Director",
          iWant: "to launch an annual performance review cycle across all 1,200 employees",
          soThat: "managers and reports complete structured reviews within the 4-week window",
          acceptanceCriteria: [
            "Cycle launched with a single action by HR Director",
            "Email reminders sent at T-14d, T-7d, T-2d",
            "HR Admin sees live completion dashboard",
            "Reviews locked from editing after cycle close",
          ],
          priority: "could",
          storyPoints: 8,
          status: "backlog",
          startDate: d(35),
          dueDate: d(60),
          estimatedDays: 25,
        },
        {
          id: "us-10",
          title: "SOX immutable payroll audit log",
          asA: "Finance Controller",
          iWant: "every payroll calculation step logged immutably with user and timestamp",
          soThat: "we pass the annual SOX audit without manual evidence gathering",
          acceptanceCriteria: [
            "Log entries cannot be edited or deleted by any user role",
            "Log retained for 7 years minimum with automatic archival",
            "Audit log export available in CSV, PDF, and JSON",
            "External auditor read-only role can access logs without full system access",
          ],
          priority: "must",
          storyPoints: 5,
          status: "backlog",
          startDate: d(20),
          dueDate: d(38),
          estimatedDays: 18,
        },
      ],
    },
    { headers: auth }
  );

  const projectId: string = project.id;

  // Helper for non-fatal calls
  const tryPost = async (url: string, body: object) => {
    try {
      const { data } = await axios.post(url, body, { headers: auth });
      return data;
    } catch {
      return null;
    }
  };

  // ── 2. Requirements ──────────────────────────────────────────────────────────
  const requirements = [
    {
      title: "Payroll processing — fortnightly and monthly cycles",
      raw_text: "The system must support both fortnightly and monthly payroll cycles. Each cycle must complete calculation and produce payslips within 4 hours of run initiation.",
      req_type: "functional", priority: "must", domain: "payroll",
    },
    {
      title: "SOX-compliant immutable audit trail",
      raw_text: "All payroll calculation inputs, intermediate values, and outputs must be logged immutably with user ID and UTC timestamp. Logs retained 7 years.",
      req_type: "non-functional", priority: "must", domain: "compliance",
    },
    {
      title: "Azure AD SSO with role-based access",
      raw_text: "All authentication must route through Azure AD via OIDC. Role mapping must support Employee, Manager, HR Admin, Finance Controller, and Super Admin. MFA policy inherited from Azure AD.",
      req_type: "functional", priority: "must", domain: "security",
    },
    {
      title: "Leave balance real-time calculation",
      raw_text: "Leave balances must be calculated in real-time, incorporating accrual rules, approved leaves, and state/country public holiday calendars. Stale cache not acceptable.",
      req_type: "functional", priority: "must", domain: "leave",
    },
    {
      title: "System response time SLA",
      raw_text: "All page loads must complete within 2 seconds for 95th percentile under normal load (500 concurrent users). Payroll report generation is exempt and must complete within 5 minutes.",
      req_type: "non-functional", priority: "must", domain: "performance",
    },
    {
      title: "PeopleSoft data migration — 7 years history",
      raw_text: "Migration must transfer all employee records, payroll history, and leave balances from PeopleSoft 9.1 covering 2018-2025. Validation: zero employee count discrepancy, payroll variance < 0.01%.",
      req_type: "constraint", priority: "must", domain: "migration",
    },
  ];

  for (const req of requirements) {
    await tryPost(`${BASE}/api/tools/requirements`, { ...req, project_id: projectId });
  }

  // ── 3. CAPA entries ──────────────────────────────────────────────────────────
  const capas = [
    {
      project_id: projectId,
      title: "Payroll reconciliation report — 3 mismatches found in Sprint 3 demo",
      description: "During Sprint 3 demo, Finance Controller identified 3 employee records where the variance delta was computed in local currency but compared against the AUD threshold ($50). This caused correct variances to be flagged and valid ones to be missed.",
      root_cause: "Currency conversion was applied after threshold comparison instead of before. The currency normalization function was called in the wrong sequence in the calculation pipeline.",
      corrective_action: "Reorder the calculation pipeline: normalize all amounts to AUD before any threshold comparison. Add unit tests for multi-currency edge cases.",
      preventive_action: "Add a pre-flight check in the payroll run that validates currency normalization is applied before all comparison operations. Add this to the definition of done for all payroll-related stories.",
      status: "open",
      priority: "high",
      due_date: d(5),
      raised_by: "Priya Nair",
    },
    {
      project_id: projectId,
      title: "Leave overlap warning not triggering for offshore teams",
      description: "The overlapping leave detection feature is not applying the 30% threshold correctly for the Singapore office because public holidays are not excluded from the headcount calculation denominator.",
      root_cause: "Public holiday calendar for SGP was not loaded during threshold calculation. The team headcount for a given week incorrectly included employees on statutory leave.",
      corrective_action: "Update the team headcount query to exclude employees on public holiday for the relevant jurisdiction. Reload SGP calendar data.",
      preventive_action: "Add acceptance test for each jurisdiction: SGP, NSW, VIC, QLD, WA, NY. These tests must pass before any leave-related story is marked done.",
      status: "open",
      priority: "medium",
      due_date: d(12),
      raised_by: "Marcus Webb",
    },
  ];

  for (const capa of capas) {
    await tryPost(`${BASE}/api/tools/capa`, capa);
  }

  // ── 4. Suppliers ─────────────────────────────────────────────────────────────
  const suppliers = [
    {
      project_id: projectId,
      name: "Workday Inc.",
      category: "HRMS Platform",
      risk_tier: "critical",
      contact_email: "enterprise@workday.com",
      notes: "Primary vendor. SLA: 99.9% uptime. Contract signed 2025-06-01. Implementation partner: Accenture.",
    },
    {
      project_id: projectId,
      name: "Accenture — HR Transformation Practice",
      category: "Implementation Partner",
      risk_tier: "major",
      contact_email: "hrtech@accenture.com",
      notes: "8-person team embedded on-site 3 days/week. Fixed-price contract to go-live. Key contact: Raj Sharma.",
    },
    {
      project_id: projectId,
      name: "CloudMigrate PTY LTD",
      category: "Data Migration",
      risk_tier: "major",
      contact_email: "projects@cloudmigrate.com.au",
      notes: "Specialist PeopleSoft-to-Workday migration tooling. Prior experience: 4 Australian enterprise migrations. Engaged on T&M basis.",
    },
  ];

  for (const supplier of suppliers) {
    await tryPost(`${BASE}/api/tools/suppliers`, supplier);
  }

  // ── 5. Test Cases ────────────────────────────────────────────────────────────
  const testCases = [
    {
      project_id: projectId,
      title: "Payroll run — fortnightly cycle end-to-end",
      description: "Verify complete fortnightly payroll run for a 50-employee subset. Covers period lock, calculation, variance report generation, and payslip distribution.",
      steps: [
        "Log in as Finance Controller",
        "Navigate to Payroll → Run Management",
        "Select current fortnightly period and click Initiate Run",
        "Verify period lock banner appears on all timesheet views",
        "Confirm calculation completes within 4 hours",
        "Download variance report and verify format",
        "Confirm payslips appear in employee self-service within 2 hours of run completion",
      ],
      expected_result: "Run completes without error. Variance report shows zero unexplained deltas > $50. All 50 payslips available in employee portal.",
      priority: "must",
      status: "pending",
    },
    {
      project_id: projectId,
      title: "Azure AD SSO — role mapping validation",
      description: "Validate all 5 role mappings from Azure AD groups to HRMS roles. Includes MFA enforcement and deprovisioning latency.",
      steps: [
        "In Azure AD, assign test user to Employee group",
        "Log into HRMS — verify Employee role (no admin menus visible)",
        "Repeat for Manager, HR Admin, Finance Controller, Super Admin",
        "Enable MFA on test user in Azure AD — verify HRMS enforces it",
        "Disable test user in Azure AD — measure time until HRMS access revoked",
      ],
      expected_result: "All 5 role mappings correct. MFA enforced. Deprovisioning completes within 15 minutes.",
      priority: "must",
      status: "pending",
    },
    {
      project_id: projectId,
      title: "Leave request — 30% overlap warning",
      description: "Test the overlapping leave conflict detection for a team of 10 where 3 members (30%) request the same date.",
      steps: [
        "Create a team of 10 in test environment",
        "Log in as Employee 1, submit leave for 2026-08-15",
        "Log in as Employee 2, submit leave for 2026-08-15",
        "Log in as Employee 3, submit leave for 2026-08-15",
        "Log in as Manager, attempt to approve Employee 3's leave",
        "Verify warning banner appears before approval action",
        "Approve and verify HR Admin receives weekly digest",
      ],
      expected_result: "Warning displays at exactly 30% threshold. Manager can still approve. Digest scheduled.",
      priority: "should",
      status: "pending",
    },
    {
      project_id: projectId,
      title: "Performance — 500 concurrent users load test",
      description: "Verify system meets 2-second p95 response SLA under 500 concurrent simulated users using k6.",
      steps: [
        "Deploy to staging environment with production-equivalent data volume",
        "Run k6 script with 500 VUs ramping over 2 minutes",
        "Execute mixed scenario: 60% leave views, 25% payslip downloads, 15% timesheet edits",
        "Collect p50, p95, p99 response times",
        "Check error rate — must be < 0.1%",
      ],
      expected_result: "p95 response time ≤ 2,000ms. Error rate < 0.1%. No OOM or connection pool exhaustion.",
      priority: "must",
      status: "pending",
    },
  ];

  for (const tc of testCases) {
    await tryPost(`${BASE}/api/tools/test-cases`, tc);
  }

  // ── 6. Evolution Log ─────────────────────────────────────────────────────────
  const evolutionEntries = [
    {
      project_id: projectId,
      change_type: "scope_add",
      title: "Add Singapore SGD payroll support",
      description: "Originally scoped for AUD only. Finance confirmed 47 employees are based in Singapore and paid in SGD. Scope extended to cover SGD + USD (New York office).",
      requested_by: "Priya Nair",
      impact: "Adds 8 story points to Sprint 5. No timeline impact — absorbed in existing sprint capacity.",
      status: "approved",
      created_at: d(-22),
    },
    {
      project_id: projectId,
      change_type: "requirement_change",
      title: "SOX log retention extended from 5 to 7 years",
      description: "Legal confirmed ATO updated guidance on payroll record retention. Previous requirement stated 5 years. Updated to 7 years to align with new guidance effective 2025-07-01.",
      requested_by: "Priya Nair",
      impact: "Storage cost increase ~$3,200/yr. No development change required — config-only update to archival policy.",
      status: "approved",
      created_at: d(-15),
    },
    {
      project_id: projectId,
      change_type: "scope_remove",
      title: "Remove Learning Management System integration from Phase 1",
      description: "LMS vendor contract not finalised in time. Linda Tran approved deferral to Phase 2 to avoid blocking HRMS go-live.",
      requested_by: "Linda Tran",
      impact: "Removes 21 story points from backlog. Saves $85K in Phase 1 integration costs.",
      status: "approved",
      created_at: d(-8),
    },
  ];

  for (const entry of evolutionEntries) {
    await tryPost(`${BASE}/api/tools/evolution-logs`, entry);
  }

  // ── 7. Knowledge Base documents ──────────────────────────────────────────────
  const kbDocs = [
    {
      project_id: projectId,
      title: "Payroll Calculation Rules — HRMS Modernization",
      doc_type: "policy",
      source: "HR Policy Manual v4.2",
      content: `# Payroll Calculation Rules

## Pay Cycles
- Monthly: Last business day of each month. Cut-off: 5 business days before.
- Fortnightly: Every second Friday. Cut-off: 3 business days before.

## Superannuation
- Rate: 11.5% (2025 SG rate) of Ordinary Time Earnings
- Paid: Monthly, within 28 days of quarter end
- Threshold: No super on earnings < $450/month (removed from Jul 2022)

## Leave Loading
- Annual Leave Loading: 17.5% on leave pay if specified in EBA
- Long Service Leave: Applicable after 10 years. Pro-rata available after 7 years resignation.

## Tax Withholding
- PAYG Withholding per ATO Tax Tables (updated Jul 2025)
- HELP/HECS compulsory repayment applies where income > $54,435

## Allowances (not subject to super)
- Tool allowance: $12.50/week (trades roles only)
- Vehicle allowance: $0.96/km (ATO rate 2025)
- Meal allowance: $35.50/day (travel >50km from usual work location)`,
    },
    {
      project_id: projectId,
      title: "Leave Policy — 18 Leave Types Reference",
      doc_type: "policy",
      source: "HR Leave Policy v3.0",
      content: `# Leave Type Reference

| Code | Name | Days/Year | Accrual | Carries Over |
|------|------|-----------|---------|--------------|
| AL | Annual Leave | 20 | Weekly | Yes (max 40d) |
| SL | Sick Leave | 10 | Weekly | Yes (unlimited) |
| CSL | Carer's Leave | 10 | Shared with SL | No |
| ML | Maternity Leave | 78 | N/A | No |
| PL | Paternity Leave | 10 | N/A | No |
| CL | Compassionate Leave | 3 per occasion | N/A | No |
| JSL | Jury Service Leave | Duration | N/A | No |
| VL | Volunteer Leave | 3 | Annual | No |
| LSL | Long Service Leave | 65 after 10yr | Tenure | Yes |
| WIL | Workers Comp Leave | Duration | N/A | N/A |
| SBL | Study Leave | 5 | Annual | No |
| FL | Flood/Emergency Leave | 3 | Annual | No |
| DV | Family Violence Leave | 10 | Annual | No (confidential) |
| RD | Rostered Day Off | Per roster | Weekly | No |
| TOIL | Time Off In Lieu | Per agreement | Per claim | 90d expiry |
| UPL | Unpaid Leave | By approval | N/A | N/A |
| LWOP | Leave Without Pay | By approval | N/A | N/A |
| MIL | Military Leave | Duration | N/A | No |

All leave types must be correctly mapped in the HRMS leave module.`,
    },
    {
      project_id: projectId,
      title: "Data Migration — PeopleSoft Field Mapping",
      doc_type: "technical",
      source: "CloudMigrate PTY LTD — Migration Spec v1.1",
      content: `# PeopleSoft to Workday Field Mapping

## Employee Master Record
| PeopleSoft Field | Workday Field | Transform |
|---|---|---|
| EMPLID | Worker_ID | Prepend "PS-" |
| LAST_NAME | Last_Name | None |
| FIRST_NAME | First_Name | None |
| HIRE_DT | Hire_Date | Date format YYYY-MM-DD |
| TERMINATION_DT | Termination_Date | Null if active |
| DEPT_ID | Cost_Center_ID | Map via lookup table |
| JOB_CODE | Job_Profile_ID | Map via lookup table |
| COMP_RATE | Annual_Pay | ×26 if fortnightly |
| CURRENCY_CD | Currency | ISO 4217 code |

## Data Quality Rules
- EMPLID must be unique across all schema versions
- Employees with TERMINATION_DT < 2018-01-01 are excluded (7-year window)
- COMP_RATE must be positive and > minimum wage for jurisdiction

## Known Issues
- Schema V1 (2018–2021): DEPT_ID uses numeric codes, lookup table provided by HR
- Schema V2 (2021–2023): 127 duplicate EMPLID records due to system migration bug — resolution: keep record with latest HIRE_DT
- Schema V3 (2023–present): Clean, no known issues`,
    },
  ];

  for (const doc of kbDocs) {
    await tryPost(`${BASE}/api/tools/documents`, doc);
  }

  // ── 8. Compliance Rules ──────────────────────────────────────────────────────
  const rules = [
    {
      project_id: projectId,
      name: "SOX — Payroll Immutable Audit Log",
      description: "All payroll calculation events must be logged immutably. No delete or edit operations permitted on audit log by any user role. Failure constitutes a SOX control deficiency.",
      domain: "finance",
      market: "AU",
      channel: "internal",
      contains_phi: false,
    },
    {
      project_id: projectId,
      name: "Privacy Act 1988 — Employee PII Handling",
      description: "All employee personal data (name, address, TFN, bank details) must be encrypted at rest (AES-256) and in transit (TLS 1.3+). Access logged. Data subject requests fulfilled within 30 days.",
      domain: "privacy",
      market: "AU",
      channel: "internal",
      contains_phi: true,
    },
  ];

  for (const rule of rules) {
    await tryPost(`${BASE}/api/tools/rules`, rule);
  }

  return projectId;
}
